import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { compact, isEqual } from 'lodash-es';
import { PracticeManagement } from '~/types/api.model';
export var ServiceIntegrationType = /*#__PURE__*/ function(ServiceIntegrationType) {
    ServiceIntegrationType["Qbo"] = "qbo";
    ServiceIntegrationType["Karbon"] = "karbon";
    ServiceIntegrationType["ClientHub"] = "clientHub";
    ServiceIntegrationType["FinancialCents"] = "financialCents";
    ServiceIntegrationType["Keeper"] = "keeper";
    ServiceIntegrationType["Xero"] = "xero";
    ServiceIntegrationType["StanfordTax"] = "stanfordTax";
    return ServiceIntegrationType;
}({});
export var StanfordTaxReturnType = /*#__PURE__*/ function(StanfordTaxReturnType) {
    StanfordTaxReturnType["Individual"] = "1040";
    StanfordTaxReturnType["CCorporation"] = "1120";
    StanfordTaxReturnType["SCorporation"] = "1120S";
    StanfordTaxReturnType["Partnership"] = "1065";
    return StanfordTaxReturnType;
}({});
export var IntegratedClassType = /*#__PURE__*/ function(IntegratedClassType) {
    IntegratedClassType["Set"] = "set";
    IntegratedClassType["Unset"] = "unset";
    return IntegratedClassType;
}({});
export var AssignedUserType = /*#__PURE__*/ function(AssignedUserType) {
    AssignedUserType["Assigned"] = "assigned";
    AssignedUserType["Unassigned"] = "unassigned";
    return AssignedUserType;
}({});
const hasAssignedMembers = (teamMembers)=>Object.values(teamMembers).some((member)=>(member === null || member === void 0 ? void 0 : member.type) === "assigned");
export const isStanfordTaxReturnType = (value)=>Object.values(StanfordTaxReturnType).includes(value);
export const FIRST_STANFORD_TAX_YEAR = 2020;
const LAST_FOUR_DIGIT_YEAR = 9999;
const isStanfordTaxYear = (value)=>Number.isInteger(value) && value >= FIRST_STANFORD_TAX_YEAR && value <= LAST_FOUR_DIGIT_YEAR;
const stanfordTaxMappingFromDTO = (param)=>{
    let { value } = param;
    const { taxReturnType, taxYear } = value;
    if (taxReturnType == null && taxYear == null) return {};
    if (taxReturnType != null && isStanfordTaxReturnType(taxReturnType) && taxYear != null && isStanfordTaxYear(taxYear)) {
        return {
            mapping: {
                returnType: taxReturnType,
                taxYear
            }
        };
    }
    return {
        invalid: value
    };
};
export class ServiceIntegrationsModel {
    static fromDTO(serviceIntegrations) {
        if (!serviceIntegrations) return new ServiceIntegrationsModel({});
        let qbo = undefined;
        let xero = undefined;
        let karbon = undefined;
        let clientHub = undefined;
        let financialCents = undefined;
        let keeper = undefined;
        let stanfordTax = undefined;
        let stanfordTaxInvalid = undefined;
        serviceIntegrations.forEach((integration)=>{
            if (integration.type === "qbo") {
                qbo = QboServiceIntegrationModel.fromDTO(integration);
            } else if (integration.type === "xero") {
                xero = XeroServiceIntegrationModel.fromDTO(integration);
            } else if (integration.type === "financialCents") {
                financialCents = PracticeManagementServiceIntegrationModel.fromDTO(integration);
            } else if (integration.type === "karbon") {
                karbon = PracticeManagementServiceIntegrationModel.fromDTO(integration);
            } else if (integration.type === "clientHub") {
                clientHub = PracticeManagementServiceIntegrationModel.fromDTO(integration);
            } else if (integration.type === "keeper") {
                keeper = PracticeManagementServiceIntegrationModel.fromDTO(integration);
            } else if (integration.type === "stanfordTax") {
                ({ mapping: stanfordTax, invalid: stanfordTaxInvalid } = stanfordTaxMappingFromDTO(integration));
            }
        });
        return new ServiceIntegrationsModel({
            qbo,
            financialCents,
            karbon,
            clientHub,
            keeper,
            xero,
            stanfordTax,
            stanfordTaxInvalid
        });
    }
    toJSON() {
        var _this_qbo, _this_karbon, _this_clientHub, _this_financialCents, _this_keeper, _this_xero;
        const json = compact([
            (_this_qbo = this.qbo) === null || _this_qbo === void 0 ? void 0 : _this_qbo.toJSON(),
            (_this_karbon = this.karbon) === null || _this_karbon === void 0 ? void 0 : _this_karbon.toJSON(),
            (_this_clientHub = this.clientHub) === null || _this_clientHub === void 0 ? void 0 : _this_clientHub.toJSON(),
            (_this_financialCents = this.financialCents) === null || _this_financialCents === void 0 ? void 0 : _this_financialCents.toJSON(),
            (_this_keeper = this.keeper) === null || _this_keeper === void 0 ? void 0 : _this_keeper.toJSON(),
            (_this_xero = this.xero) === null || _this_xero === void 0 ? void 0 : _this_xero.toJSON()
        ]);
        return json.length ? json : undefined;
    }
    hasIntegrations() {
        return !!(this.qbo || this.financialCents || this.karbon || this.clientHub || this.keeper || this.xero);
    }
    stanfordTaxMapping() {
        return this.stanfordTax;
    }
    invalidStanfordTaxMapping() {
        return this.stanfordTaxInvalid;
    }
    stanfordTaxMappingChange() {
        return this.stanfordTaxChange;
    }
    setStanfordTaxMapping(mapping) {
        if (isEqual(mapping, this.stanfordTax)) return this;
        return this.cloneWithChanges({
            stanfordTax: mapping,
            stanfordTaxChange: {
                mapping
            }
        });
    }
    inheritStanfordTaxMapping() {
        if (!this.stanfordTax) return this;
        return this.cloneWithChanges({
            stanfordTaxChange: {
                mapping: this.stanfordTax
            }
        });
    }
    openStanfordTaxSetting() {
        return this.cloneWithChanges({
            isStanfordTaxSettingOpen: true
        });
    }
    removeStanfordTaxSetting() {
        return this.setStanfordTaxMapping(undefined).cloneWithChanges({
            isStanfordTaxSettingOpen: false
        });
    }
    isStanfordTaxSettingActive() {
        return this.stanfordTax != null || this.isStanfordTaxSettingOpen;
    }
    stanfordTaxInstanceEntry() {
        if (!this.stanfordTax) {
            return this.hasHeldStanfordTax ? {
                type: "stanfordTax",
                value: {}
            } : undefined;
        }
        return {
            type: "stanfordTax",
            value: {
                taxReturnType: this.stanfordTax.returnType,
                taxYear: this.stanfordTax.taxYear
            }
        };
    }
    xeroIntegratedId() {
        var _this_xero;
        return ((_this_xero = this.xero) === null || _this_xero === void 0 ? void 0 : _this_xero.integratedId()) || undefined;
    }
    qboIntegratedId() {
        var _this_qbo;
        return ((_this_qbo = this.qbo) === null || _this_qbo === void 0 ? void 0 : _this_qbo.integratedId()) || undefined;
    }
    qboIntegratedClassId() {
        return this.qbo ? this.qbo.integratedClassId() : null;
    }
    isQboClassSet() {
        var _ref;
        var _this_qbo;
        return (_ref = (_this_qbo = this.qbo) === null || _this_qbo === void 0 ? void 0 : _this_qbo.hasQboClassId()) !== null && _ref !== void 0 ? _ref : false;
    }
    qboIntegratedType() {
        var _this_qbo;
        return ((_this_qbo = this.qbo) === null || _this_qbo === void 0 ? void 0 : _this_qbo.integratedType()) || undefined;
    }
    financialCentsIntegration() {
        return this.financialCents;
    }
    karbonIntegration() {
        return this.karbon;
    }
    clientHubIntegration() {
        return this.clientHub;
    }
    keeperIntegration() {
        return this.keeper;
    }
    financialCentsIntegratedIds() {
        var _this_financialCents;
        return ((_this_financialCents = this.financialCents) === null || _this_financialCents === void 0 ? void 0 : _this_financialCents.getIntegratedIds()) || [];
    }
    karbonIntegratedIds() {
        var _this_karbon;
        return ((_this_karbon = this.karbon) === null || _this_karbon === void 0 ? void 0 : _this_karbon.getIntegratedIds()) || [];
    }
    clientHubIntegratedIds() {
        var _this_clientHub;
        return ((_this_clientHub = this.clientHub) === null || _this_clientHub === void 0 ? void 0 : _this_clientHub.getIntegratedIds()) || [];
    }
    keeperIntegratedIds() {
        var _this_keeper;
        return ((_this_keeper = this.keeper) === null || _this_keeper === void 0 ? void 0 : _this_keeper.getIntegratedIds()) || [];
    }
    setFinancialCentsIntegration(financialCents) {
        let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return this.setInnerIntegration("financialCents", financialCents, options);
    }
    setKarbonIntegration(karbon) {
        let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return this.setInnerIntegration("karbon", karbon, options);
    }
    setKeeperIntegration(keeper) {
        let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return this.setInnerIntegration("keeper", keeper, options);
    }
    setClientHubIntegration(clientHub) {
        let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return this.setInnerIntegration("clientHub", clientHub, options);
    }
    setQboIntegration(qbo) {
        return this.cloneWithChanges({
            qbo
        });
    }
    setXeroIntegration(xero) {
        return this.cloneWithChanges({
            xero
        });
    }
    setFinancialCentsIntegratedIds(integratedIds) {
        const financialCents = this.financialCents ? this.financialCents.setIntegratedIds(integratedIds) : integratedIds.length ? PracticeManagementServiceIntegrationModel.makeFinancialCents({
            integratedIds
        }) : undefined;
        return this.cloneWithChanges({
            financialCents
        });
    }
    setKarbonIntegratedIds(integratedIds) {
        const karbon = this.karbon ? this.karbon.setIntegratedIds(integratedIds) : integratedIds.length ? PracticeManagementServiceIntegrationModel.makeKarbon({
            integratedIds
        }) : undefined;
        return this.cloneWithChanges({
            karbon
        });
    }
    setClientHubIntegratedIds(integratedIds) {
        const clientHub = this.clientHub ? this.clientHub.setIntegratedIds(integratedIds) : integratedIds.length ? PracticeManagementServiceIntegrationModel.makeClientHub({
            integratedIds
        }) : undefined;
        return this.cloneWithChanges({
            clientHub
        });
    }
    setKeeperIntegratedIds(integratedIds) {
        const keeper = this.keeper ? this.keeper.setIntegratedIds(integratedIds) : integratedIds.length ? PracticeManagementServiceIntegrationModel.makeKeeper({
            integratedIds
        }) : undefined;
        return this.cloneWithChanges({
            keeper
        });
    }
    setQboIntegratedClass(integratedClass) {
        if (this.qbo) {
            return this.cloneWithChanges({
                qbo: this.qbo.setIntegratedClass(integratedClass)
            });
        }
        if (integratedClass != null) {
            return this.cloneWithChanges({
                qbo: new QboServiceIntegrationModel(null, integratedClass.type === "set" ? integratedClass.set.id : null, undefined)
            });
        }
        return this.cloneWithChanges({
            qbo: undefined
        });
    }
    setXeroIntegratedId(integratedId) {
        const xero = this.xero ? this.xero.setIntegratedId(integratedId) : integratedId != null ? new XeroServiceIntegrationModel(integratedId) : undefined;
        return this.cloneWithChanges({
            xero
        });
    }
    setQboIntegratedId(integratedId) {
        const qbo = this.qbo ? this.qbo.setIntegratedId(integratedId) : integratedId != null ? new QboServiceIntegrationModel(integratedId, null, undefined) : undefined;
        return this.cloneWithChanges({
            qbo
        });
    }
    setQboIntegratedType(type) {
        const qbo = this.qbo ? this.qbo.setIntegratedType(type) : type != null ? new QboServiceIntegrationModel(null, null, type) : undefined;
        return this.cloneWithChanges({
            qbo
        });
    }
    setInnerIntegration(integrationKey, params, param) {
        let { preserveExistingTeamMembers: assignTeamMembers } = param;
        var _ref, _ref1, _ref2, _ref3;
        var _this_integrationKey, _this_integrationKey1, _this_integrationKey2;
        const existingTeamMembers = (_ref = (_this_integrationKey = this[integrationKey]) === null || _this_integrationKey === void 0 ? void 0 : _this_integrationKey.getTeamMembers()) !== null && _ref !== void 0 ? _ref : {};
        const existingIntegratedIds = (_ref1 = (_this_integrationKey1 = this[integrationKey]) === null || _this_integrationKey1 === void 0 ? void 0 : _this_integrationKey1.getIntegratedIds()) !== null && _ref1 !== void 0 ? _ref1 : [];
        const existingEnabled = (_ref2 = (_this_integrationKey2 = this[integrationKey]) === null || _this_integrationKey2 === void 0 ? void 0 : _this_integrationKey2.isEnabled()) !== null && _ref2 !== void 0 ? _ref2 : false;
        const newTeamMembers = params === null || params === void 0 ? void 0 : params.teamMembers;
        const newIntegration = new PracticeManagementServiceIntegrationModel(integrationKey, (_ref3 = params === null || params === void 0 ? void 0 : params.integratedIds) !== null && _ref3 !== void 0 ? _ref3 : existingIntegratedIds, assignTeamMembers && newTeamMembers ? _object_spread({}, existingTeamMembers, newTeamMembers) : newTeamMembers !== null && newTeamMembers !== void 0 ? newTeamMembers : existingTeamMembers, (params === null || params === void 0 ? void 0 : params.enabled) !== undefined ? params.enabled : existingEnabled);
        return this.cloneWithChanges({
            [integrationKey]: newIntegration
        });
    }
    cloneWithChanges() {
        let changes = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        var _changes_isStanfordTaxSettingOpen;
        return new ServiceIntegrationsModel({
            qbo: 'qbo' in changes ? changes.qbo : this.qbo,
            karbon: 'karbon' in changes ? changes.karbon : this.karbon,
            clientHub: 'clientHub' in changes ? changes.clientHub : this.clientHub,
            financialCents: 'financialCents' in changes ? changes.financialCents : this.financialCents,
            keeper: 'keeper' in changes ? changes.keeper : this.keeper,
            xero: 'xero' in changes ? changes.xero : this.xero,
            stanfordTax: 'stanfordTax' in changes ? changes.stanfordTax : this.stanfordTax,
            hasHeldStanfordTax: this.hasHeldStanfordTax,
            isStanfordTaxSettingOpen: (_changes_isStanfordTaxSettingOpen = changes.isStanfordTaxSettingOpen) !== null && _changes_isStanfordTaxSettingOpen !== void 0 ? _changes_isStanfordTaxSettingOpen : this.isStanfordTaxSettingOpen,
            stanfordTaxChange: 'stanfordTaxChange' in changes ? changes.stanfordTaxChange : this.stanfordTaxChange,
            stanfordTaxInvalid: this.stanfordTaxInvalid
        });
    }
    constructor(data){
        var _data_hasHeldStanfordTax, _data_isStanfordTaxSettingOpen;
        _define_property(this, "qbo", void 0);
        _define_property(this, "karbon", void 0);
        _define_property(this, "clientHub", void 0);
        _define_property(this, "financialCents", void 0);
        _define_property(this, "keeper", void 0);
        _define_property(this, "xero", void 0);
        _define_property(this, "stanfordTax", void 0);
        _define_property(this, "hasHeldStanfordTax", void 0);
        _define_property(this, "isStanfordTaxSettingOpen", void 0);
        _define_property(this, "stanfordTaxChange", void 0);
        _define_property(this, "stanfordTaxInvalid", void 0);
        this.qbo = data.qbo;
        this.karbon = data.karbon;
        this.clientHub = data.clientHub;
        this.financialCents = data.financialCents;
        this.keeper = data.keeper;
        this.xero = data.xero;
        this.stanfordTax = data.stanfordTax;
        this.hasHeldStanfordTax = ((_data_hasHeldStanfordTax = data.hasHeldStanfordTax) !== null && _data_hasHeldStanfordTax !== void 0 ? _data_hasHeldStanfordTax : false) || data.stanfordTax != null;
        this.isStanfordTaxSettingOpen = (_data_isStanfordTaxSettingOpen = data.isStanfordTaxSettingOpen) !== null && _data_isStanfordTaxSettingOpen !== void 0 ? _data_isStanfordTaxSettingOpen : false;
        this.stanfordTaxChange = data.stanfordTaxChange;
        this.stanfordTaxInvalid = data.stanfordTaxInvalid;
    }
}
export class XeroServiceIntegrationModel {
    static fromDTO(dto) {
        return new XeroServiceIntegrationModel(dto.value.integratedId);
    }
    toJSON() {
        return {
            type: "xero",
            value: {
                integratedId: this._integratedId
            }
        };
    }
    integratedId() {
        return this._integratedId;
    }
    setIntegratedId(integratedId) {
        return new XeroServiceIntegrationModel(integratedId || null);
    }
    constructor(_integratedId){
        _define_property(this, "_integratedId", void 0);
        this._integratedId = _integratedId;
    }
}
export class QboServiceIntegrationModel {
    static fromDTO(dto) {
        var _dto_value_integratedClass;
        const integratedClassId = ((_dto_value_integratedClass = dto.value.integratedClass) === null || _dto_value_integratedClass === void 0 ? void 0 : _dto_value_integratedClass.type) === "set" ? dto.value.integratedClass.set.id : null;
        return new QboServiceIntegrationModel(dto.value.integratedId, integratedClassId, dto.value.type);
    }
    toJSON() {
        return {
            type: "qbo",
            value: {
                integratedId: this._integratedId,
                type: this._type,
                integratedClass: this._integratedClassId != null ? {
                    type: "set",
                    set: {
                        id: this._integratedClassId
                    }
                } : {
                    type: "unset"
                }
            }
        };
    }
    integratedId() {
        return this._integratedId;
    }
    integratedClassId() {
        return this._integratedClassId;
    }
    hasQboClassId() {
        return this._integratedClassId != null;
    }
    integratedType() {
        return this._type;
    }
    setIntegratedClass(integratedClass) {
        const integratedClassId = (integratedClass === null || integratedClass === void 0 ? void 0 : integratedClass.type) === "set" ? integratedClass.set.id : null;
        return new QboServiceIntegrationModel(this._integratedId, integratedClassId, this._type);
    }
    setIntegratedId(integratedId) {
        return new QboServiceIntegrationModel(integratedId || null, this._integratedClassId, this._type);
    }
    setIntegratedType(type) {
        return new QboServiceIntegrationModel(this._integratedId, this._integratedClassId, type);
    }
    constructor(_integratedId, _integratedClassId, _type){
        _define_property(this, "_integratedId", void 0);
        _define_property(this, "_integratedClassId", void 0);
        _define_property(this, "_type", void 0);
        this._integratedId = _integratedId;
        this._integratedClassId = _integratedClassId;
        this._type = _type;
    }
}
export class PracticeManagementServiceIntegrationModel {
    static fromDTO(pm) {
        var _pm_value_enabled;
        const teamMembers = pm.value.teamMembers || {};
        const enabled = (_pm_value_enabled = pm.value.enabled) !== null && _pm_value_enabled !== void 0 ? _pm_value_enabled : hasAssignedMembers(teamMembers);
        return new PracticeManagementServiceIntegrationModel(pm.type, pm.value.integratedIds, teamMembers, enabled);
    }
    static makeFinancialCents(param) {
        let { integratedIds = null, teamMembers = {}, enabled } = param;
        const resolvedEnabled = typeof enabled === 'boolean' ? enabled : hasAssignedMembers(teamMembers);
        return new PracticeManagementServiceIntegrationModel("financialCents", integratedIds, teamMembers, resolvedEnabled);
    }
    static makeKarbon(param) {
        let { teamMembers = {}, integratedIds = null, enabled } = param;
        const resolvedEnabled = typeof enabled === 'boolean' ? enabled : hasAssignedMembers(teamMembers);
        return new PracticeManagementServiceIntegrationModel("karbon", integratedIds, teamMembers, resolvedEnabled);
    }
    static makeClientHub(param) {
        let { teamMembers = {}, integratedIds = null, enabled } = param;
        const resolvedEnabled = typeof enabled === 'boolean' ? enabled : hasAssignedMembers(teamMembers);
        return new PracticeManagementServiceIntegrationModel("clientHub", integratedIds, teamMembers, resolvedEnabled);
    }
    static makeKeeper(param) {
        let { teamMembers = {}, integratedIds = null, enabled } = param;
        const resolvedEnabled = typeof enabled === 'boolean' ? enabled : hasAssignedMembers(teamMembers);
        return new PracticeManagementServiceIntegrationModel("keeper", integratedIds, teamMembers, resolvedEnabled);
    }
    toJSON() {
        return {
            type: this.type,
            value: {
                integratedIds: this.integratedIds,
                teamMembers: this.teamMembers
            }
        };
    }
    getIntegratedIds() {
        return this.integratedIds;
    }
    getTeamMembers() {
        return this.teamMembers;
    }
    getType() {
        return this.type;
    }
    accept(visitor) {
        switch(this.type){
            case "financialCents":
                return visitor.visitFinancialCents(this);
            case "karbon":
                return visitor.visitKarbon(this);
            case "clientHub":
                return visitor.visitClientHub(this);
            case "keeper":
                return visitor.visitKeeper(this);
        }
    }
    isEnabled() {
        return this.enabled;
    }
    hasAssignedTeamMembers() {
        return hasAssignedMembers(this.teamMembers);
    }
    assignTeamMember(roleId, userId) {
        const newTeamMembers = _object_spread_props(_object_spread({}, this.teamMembers), {
            [roleId]: userId ? makeAssignedUserAssigned(userId) : makeAssignedUserUnassigned()
        });
        return this.cloneWithChanges({
            teamMembers: newTeamMembers
        });
    }
    assignedUserId(roleId) {
        const byRoleId = this.teamMembers[roleId];
        return byRoleId && byRoleId.type === "assigned" ? byRoleId.assigned : null;
    }
    setIntegratedIds(integratedIds) {
        return this.cloneWithChanges({
            integratedIds
        });
    }
    practiceManagementType() {
        return Object.keys(PracticeManagementServiceIntegrationModel.TYPE_MAP).includes(this.type) ? PracticeManagementServiceIntegrationModel.TYPE_MAP[this.type] : null;
    }
    cloneWithChanges() {
        let changes = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        var _changes_enabled;
        return new PracticeManagementServiceIntegrationModel(this.type, changes.integratedIds || this.integratedIds, changes.teamMembers || this.teamMembers, (_changes_enabled = changes.enabled) !== null && _changes_enabled !== void 0 ? _changes_enabled : this.enabled);
    }
    constructor(type, integratedIds, teamMembers, enabled){
        _define_property(this, "type", void 0);
        _define_property(this, "integratedIds", void 0);
        _define_property(this, "teamMembers", void 0);
        _define_property(this, "enabled", void 0);
        this.type = type;
        this.integratedIds = integratedIds;
        this.teamMembers = teamMembers;
        this.enabled = enabled;
    }
}
_define_property(PracticeManagementServiceIntegrationModel, "TYPE_MAP", {
    ["financialCents"]: PracticeManagement.FinancialCents,
    ["karbon"]: PracticeManagement.Karbon,
    ["clientHub"]: PracticeManagement.ClientHub,
    ["keeper"]: PracticeManagement.Keeper
});
export function makeAssignedUserUnassigned() {
    return {
        type: "unassigned"
    };
}
export function makeAssignedUserAssigned(assigned) {
    return {
        type: "assigned",
        assigned
    };
}
